// =============================================================================
// SMART LEGAL EDITOR - Éditeur intelligent pour documents juridiques
// =============================================================================
//
// Fonctionnalités :
// - Coller du texte brut → structure automatique
// - Détection titres, sections numérotées, listes, paragraphes
// - Génération HTML en arrière-plan
// - Rendu WYSIWYG professionnel
// - Pas de balises visibles pour l'utilisateur
//
// =============================================================================

import React, { useEffect, useCallback, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  List, 
  ListOrdered, 
  Heading2,
  AlignLeft,
  AlignCenter,
  AlignJustify,
  Undo,
  Redo,
  Sparkles,
  FileText,
  Type,
  ChevronDown
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

// =============================================================================
// EXTENSION FONT SIZE
// =============================================================================

const FontSize = Extension.create({
  name: 'fontSize',
  
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize?.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    };
  },
});

// =============================================================================
// PARSER INTELLIGENT - Texte brut → HTML structuré
// =============================================================================

/**
 * Analyse et structure automatiquement un texte brut en HTML
 * 
 * Détecte :
 * - Titres en majuscules
 * - Sections numérotées (Article 1, 1., 1), I., etc.)
 * - Listes à puces (-, *, •)
 * - Paragraphes
 */
function parseTextToHTML(rawText) {
  if (!rawText || typeof rawText !== 'string') return '';
  
  const lines = rawText.split('\n');
  let html = '';
  let inList = false;
  let listType = null; // 'ul' ou 'ol'
  
  const closeList = () => {
    if (inList) {
      html += listType === 'ol' ? '</ol>' : '</ul>';
      inList = false;
      listType = null;
    }
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Ligne vide = fermer liste et ajouter espace
    if (!line) {
      closeList();
      continue;
    }
    
    // === DÉTECTION DES TITRES ===
    
    // Titre en MAJUSCULES (min 3 mots, tout en majuscules)
    const isUppercaseTitle = /^[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜÇ\s\-–:]{10,}$/.test(line) && 
                             line.split(/\s+/).length >= 2;
    
    // Article X, ARTICLE X
    const isArticleTitle = /^(ARTICLE|Article)\s+\d+/i.test(line);
    
    // Titre numéroté principal (1. TITRE, I. TITRE, etc.)
    const isNumberedTitle = /^(\d+\.|[IVXLC]+\.)\s+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜÇ]/.test(line);
    
    // Sous-titre (1.1, a), etc.)
    const isSubTitle = /^(\d+\.\d+\.?|\([a-z]\))\s+/.test(line);
    
    if (isUppercaseTitle || isArticleTitle) {
      closeList();
      html += `<h2>${escapeHtml(line)}</h2>`;
      continue;
    }
    
    if (isNumberedTitle) {
      closeList();
      html += `<h3>${escapeHtml(line)}</h3>`;
      continue;
    }
    
    if (isSubTitle) {
      closeList();
      html += `<h4>${escapeHtml(line)}</h4>`;
      continue;
    }
    
    // === DÉTECTION DES LISTES ===
    
    // Liste à puces (-, *, •, ○, ●)
    const bulletMatch = line.match(/^[-*•○●]\s+(.+)/);
    if (bulletMatch) {
      if (!inList || listType !== 'ul') {
        closeList();
        html += '<ul>';
        inList = true;
        listType = 'ul';
      }
      html += `<li>${escapeHtml(bulletMatch[1])}</li>`;
      continue;
    }
    
    // Liste numérotée dans le texte (a), b), 1), 2), etc.) - mais pas les titres
    const orderedMatch = line.match(/^([a-z]\)|[0-9]+\))\s+(.+)/i);
    if (orderedMatch && !isNumberedTitle) {
      if (!inList || listType !== 'ol') {
        closeList();
        html += '<ol>';
        inList = true;
        listType = 'ol';
      }
      html += `<li>${escapeHtml(orderedMatch[2])}</li>`;
      continue;
    }
    
    // === PARAGRAPHE STANDARD ===
    closeList();
    html += `<p>${escapeHtml(line)}</p>`;
  }
  
  closeList();
  return html;
}

/**
 * Échappe les caractères HTML dangereux
 */
function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// =============================================================================
// BARRE D'OUTILS
// =============================================================================

function EditorToolbar({ editor }) {
  if (!editor) return null;
  
  const ToolButton = ({ onClick, isActive, icon: Icon, title }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn(
        "h-8 w-8 p-0 hover:bg-[var(--oh-bg-elevated)]",
        isActive && "bg-[var(--oh-bg-elevated)] text-[var(--oh-text)]"
      )}
      title={title}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
  
  return (
    <div className="flex items-center gap-1 p-2 border-b border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] rounded-t-lg flex-wrap">
      {/* Historique */}
      <ToolButton
        onClick={() => editor.chain().focus().undo().run()}
        icon={Undo}
        title="Annuler"
      />
      <ToolButton
        onClick={() => editor.chain().focus().redo().run()}
        icon={Redo}
        title="Rétablir"
      />
      
      <div className="w-px h-6 bg-slate-600 mx-1" />
      
      {/* Formatage texte */}
      <ToolButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        icon={Bold}
        title="Gras"
      />
      <ToolButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        icon={Italic}
        title="Italique"
      />
      <ToolButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        icon={UnderlineIcon}
        title="Souligné"
      />
      
      <div className="w-px h-6 bg-slate-600 mx-1" />
      
      {/* Titres */}
      <ToolButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        icon={Heading2}
        title="Titre de section"
      />
      
      <div className="w-px h-6 bg-slate-600 mx-1" />
      
      {/* Listes */}
      <ToolButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        icon={List}
        title="Liste à puces"
      />
      <ToolButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        icon={ListOrdered}
        title="Liste numérotée"
      />
      
      <div className="w-px h-6 bg-slate-600 mx-1" />
      
      {/* Alignement */}
      <ToolButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        icon={AlignLeft}
        title="Aligner à gauche"
      />
      <ToolButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        icon={AlignCenter}
        title="Centrer"
      />
      <ToolButton
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        isActive={editor.isActive({ textAlign: 'justify' })}
        icon={AlignJustify}
        title="Justifier"
      />
      
      <div className="w-px h-6 bg-slate-600 mx-1" />
      
      {/* Taille de police */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 hover:bg-[var(--oh-bg-elevated)] gap-1"
            title="Taille de police"
          >
            <Type className="h-4 w-4" />
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <DropdownMenuItem 
            onClick={() => editor.chain().focus().setFontSize('10px').run()}
            className="text-xs hover:bg-[var(--oh-bg-elevated)] cursor-pointer"
          >
            Très petit (10px)
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => editor.chain().focus().setFontSize('12px').run()}
            className="text-sm hover:bg-[var(--oh-bg-elevated)] cursor-pointer"
          >
            Petit (12px)
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => editor.chain().focus().setFontSize('14px').run()}
            className="hover:bg-[var(--oh-bg-elevated)] cursor-pointer"
          >
            Normal (14px)
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => editor.chain().focus().setFontSize('16px').run()}
            className="text-base hover:bg-[var(--oh-bg-elevated)] cursor-pointer"
          >
            Grand (16px)
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => editor.chain().focus().setFontSize('18px').run()}
            className="text-lg hover:bg-[var(--oh-bg-elevated)] cursor-pointer"
          >
            Très grand (18px)
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => editor.chain().focus().unsetFontSize().run()}
            className="hover:bg-[var(--oh-bg-elevated)] cursor-pointer text-[var(--oh-text-secondary)]"
          >
            Réinitialiser
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export default function SmartLegalEditor({
  value = '',
  onChange,
  placeholder = "Collez votre texte ici. La structure sera détectée automatiquement...",
  label,
  description,
  minHeight = "200px",
  className
}) {
  
  // Initialiser l'éditeur TipTap
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3, 4],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      FontSize,
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'prose prose-sm prose-invert max-w-none focus:outline-none min-h-[200px] p-4',
        style: `min-height: ${minHeight}`,
      },
      // Intercepter le collage pour structurer automatiquement
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData('text/plain');
        const html = event.clipboardData?.getData('text/html');
        
        // Si c'est du texte brut (pas de HTML), on parse intelligemment
        if (text && !html) {
          event.preventDefault();
          
          // Parser le texte en HTML structuré
          const structuredHTML = parseTextToHTML(text);
          
          // Insérer le HTML structuré
          editor.commands.insertContent(structuredHTML);
          
          return true;
        }
        
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Ne pas déclencher onChange si le contenu est vide ou juste <p></p>
      if (html === '<p></p>' || html === '') {
        onChange?.('');
      } else {
        onChange?.(html);
      }
    },
  });
  
  // Synchroniser la valeur externe avec l'éditeur
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Éviter de remplacer si c'est juste un paragraphe vide
      if (value || editor.getHTML() !== '<p></p>') {
        editor.commands.setContent(value || '');
      }
    }
  }, [value, editor]);
  
  // Fonction pour structurer automatiquement le contenu actuel
  const handleAutoStructure = useCallback(() => {
    if (!editor) return;
    
    // Extraire le texte brut
    const plainText = editor.getText();
    
    if (!plainText.trim()) return;
    
    // Parser et restructurer
    const structuredHTML = parseTextToHTML(plainText);
    
    // Remplacer le contenu
    editor.commands.setContent(structuredHTML);
  }, [editor]);
  
  return (
    <div className={cn("space-y-2", className)}>
      {/* Label et description */}
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-[var(--oh-text-secondary)] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[var(--oh-accent-text)]" />
            {label}
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAutoStructure}
            className="text-xs text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
          >
            <Sparkles className="w-3 h-3 mr-1" />
            Restructurer automatiquement
          </Button>
        </div>
      )}
      {description && (
        <p className="text-xs text-[var(--oh-text-muted)]">{description}</p>
      )}
      
      {/* Éditeur */}
      <div className="border border-[var(--oh-border)] rounded-lg overflow-hidden bg-[var(--oh-bg-surface)]">
        <EditorToolbar editor={editor} />
        <EditorContent 
          editor={editor} 
          className="smart-legal-editor"
        />
      </div>
      
      {/* Info */}
      <p className="text-xs text-[var(--oh-text-muted)] flex items-center gap-1">
        <Sparkles className="w-3 h-3" />
        Collez du texte brut : les titres, sections et listes seront détectés automatiquement
      </p>
    </div>
  );
}

// =============================================================================
// STYLES CSS POUR L'ÉDITEUR
// =============================================================================

export const smartLegalEditorStyles = `
.smart-legal-editor .ProseMirror {
  min-height: 200px;
  padding: 1rem;
  color: #e2e8f0;
  font-size: 12px;
  line-height: 1.5;
}

.smart-legal-editor .ProseMirror:focus {
  outline: none;
}

.smart-legal-editor .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #64748b;
  pointer-events: none;
  height: 0;
}

/* Styles professionnels pour catalogue produit */
.smart-legal-editor .ProseMirror h2 {
  font-size: 14px;
  font-weight: 700;
  color: #f1f5f9;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #334155;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.smart-legal-editor .ProseMirror h3 {
  font-size: 13px;
  font-weight: 600;
  color: #e2e8f0;
  margin-top: 0.75rem;
  margin-bottom: 0.35rem;
}

.smart-legal-editor .ProseMirror h4 {
  font-size: 12px;
  font-weight: 600;
  color: #cbd5e1;
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
}

.smart-legal-editor .ProseMirror p {
  margin-bottom: 0.5rem;
  line-height: 1.5;
  font-size: 12px;
  color: #cbd5e1;
}

.smart-legal-editor .ProseMirror ul,
.smart-legal-editor .ProseMirror ol {
  margin-left: 1.25rem;
  margin-bottom: 0.5rem;
}

.smart-legal-editor .ProseMirror li {
  margin-bottom: 0.15rem;
  color: #cbd5e1;
  font-size: 12px;
}

.smart-legal-editor .ProseMirror ul li {
  list-style-type: disc;
}

.smart-legal-editor .ProseMirror ol li {
  list-style-type: decimal;
}

.smart-legal-editor .ProseMirror strong {
  font-weight: 600;
  color: #f1f5f9;
}

.smart-legal-editor .ProseMirror em {
  font-style: italic;
}

.smart-legal-editor .ProseMirror u {
  text-decoration: underline;
}
`;
